import {
    ACTIVE_SCREEN_KEY,
    getModuleConfig,
    getAccessToken,
    Store,
    showDialog,
    apiFetch,
    hideDialog,
    removeAllScreenMessages,
} from 'cw-ui-core';
import filter from 'lodash/filter';
import { call, put, delay } from 'redux-saga/effects';
import { LOADING_DIALOG } from 'src/components/LoadingDialog';
import { isImmutable } from 'immutable';

const identityProviderUri = (scheme, endpoint) => {
    const url =
        getModuleConfig(Store().getState().module).get('version') === 'dev'
            ? 'https://sts.connectwisedev.com'
            : 'https://auth.connectwise.com';
    return `${url}/${scheme}/${endpoint}`;
};
export const identityProviderRedirectUri = scheme => identityProviderUri(scheme, 'signin-oidc');
export const identityProviderSignoutUri = scheme => identityProviderUri(scheme, 'signout-oidc');

export const CURRENT_STS_API_VERSION = '3.0';

export const getHeaders = (version, returnHeadersObject = false, additionalHeaders) => {
    let appVersion = version;
    if (!/^\d+(\.\d+)?$/.test(appVersion)) {
        appVersion = '1.0';
    }
    const contentTypeHeader =
        additionalHeaders && additionalHeaders.includes('multipart')
            ? additionalHeaders
            : `application/json;version=${appVersion}`;
    const headersObj = {
        Authorization: `Bearer ${getAccessToken(Store().getState().oidc)}`,
        Accept: `application/json;version=${appVersion}`,
        'Content-Type': contentTypeHeader,
        x_cwauth_product: 'sts-jwt',
    };
    const headers = new Headers();
    Object.keys(headersObj).forEach(key => {
        headers.append(key, headersObj[key]);
    });
    return returnHeadersObject ? headersObj : headers;
};

export function generateBoundary() {
    return '----WebKitFormBoundary' + Math.random().toString(36).substring(2);
}

// Manually construct multipart / form - data body
export function buildMultipartBody(file, boundary) {
    const encoder = new TextEncoder();
    const chunks = [];

    // Add file field
    chunks.push(encoder.encode(`--${boundary}\r\n`));
    chunks.push(encoder.encode(`Content-Disposition: form-data; name="upload"; filename="${file.name}"\r\n`));
    chunks.push(encoder.encode(`Content-Type: ${file.type || 'application/octet-stream'}\r\n\r\n`));

    // We'll add the actual file content as a blob
    return { chunks, boundary };
}

const fetchBaseUrlConfig = (config, endpoint) => {
    if (endpoint.startsWith('/iambff')) {
        return config.get('bffUrl');
    }
    if (endpoint.startsWith('/notifications')) {
        return config.get('notificationsUrl');
    }
    return config.get('apiUrl');
};

export const getUrl = endpoint => {
    if (endpoint.startsWith('http')) {
        return endpoint;
    }
    const config = getModuleConfig(Store().getState().module);
    const apiUrl = fetchBaseUrlConfig(config, endpoint);
    if (endpoint === '/RegistrationTokens' || endpoint === '/.well-known/openid-configuration') {
        const temp = apiUrl + endpoint;
        return temp.replace('/api', '');
    }
    return apiUrl + endpoint;
};

export const getPartnerId = () => {
    return Store().getState().oidc.getIn(['user', 'profile', 'partner_id']);
};

export const getCurrentUserIdToken = () => {
    return Store().getState().oidc.getIn(['user', 'id_token']);
};

export const getCurrentUserId = () => {
    return Store().getState().oidc.getIn(['user', 'profile', 'sub']);
};

export function* loader() {
    yield delay(300);
    if (!Store().getState().dialog.get(LOADING_DIALOG)) {
        yield put(showDialog(LOADING_DIALOG));
    }
}

export function* GET(endpoint, headers) {
    const options = {
        method: 'GET',
        headers,
        // cache: 'force-cache',
    };

    const result = yield call(apiFetch, getUrl(endpoint), options);
    if (Store().getState().dialog.get(LOADING_DIALOG)) {
        yield put(hideDialog(LOADING_DIALOG));
    }
    return result;
}

export function* DELETE(endpoint, headers, body) {
    const options = {
        method: 'DELETE',
        headers,
        body: JSON.stringify(body),
    };

    const result = yield call(apiFetch, getUrl(endpoint), options);
    if (Store().getState().dialog.get(LOADING_DIALOG)) {
        yield put(hideDialog(LOADING_DIALOG));
    }
    return result;
}

export function* POST(endpoint, headers, body, isJson = true) {
    const options = {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    };

    const result = yield call(apiFetch, getUrl(endpoint), options, isJson);
    if (Store().getState().dialog.get(LOADING_DIALOG)) {
        yield put(hideDialog(LOADING_DIALOG));
    }
    return result;
}

export function* PUT(endpoint, headers, body) {
    const options = {
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
    };

    const result = yield call(apiFetch, getUrl(endpoint), options);
    if (Store().getState().dialog.get(LOADING_DIALOG)) {
        yield put(hideDialog(LOADING_DIALOG));
    }

    return result;
}

export function* POSTFORMDATA(endpoint, headers, formData) {
    const options = {
        method: 'POST',
        headers,
        body: formData,
    };

    const result = yield call(apiFetch, getUrl(endpoint), options);
    if (Store().getState().dialog.get(LOADING_DIALOG)) {
        yield put(hideDialog(LOADING_DIALOG));
    }
    return result;
}

const makePatchForArrayItem = (item, path) => {
    const position = item[0];
    const elementValue = item[1];

    if (position === 'x') {
        return {
            op: 'remove',
            path: `${path}/${position}`,
        };
    }

    const operation = position === '-' ? 'add' : 'replace';
    return {
        op: operation,
        path: `${path}/${position}`,
        value: elementValue,
    };
};

const mapToPatchReducer = (patches, value, key) => {
    let mappedPatches = patches;
    const path = `/${key}`;
    const values = isImmutable(value) ? value.toJS() : value;
    if (Array.isArray(values)) {
        mappedPatches = [...patches, ...values.map(item => makePatchForArrayItem(item, path))];
    } else {
        const operation = 'replace';
        mappedPatches.push({
            op: operation,
            path,
            value,
        });
    }
    return mappedPatches;
};

export function* PATCH(endpoint, headers, data, isDataAlreadyPatchCollection) {
    const patches = isDataAlreadyPatchCollection ? data : data.reduce(mapToPatchReducer, []);
    const sortPatches = patchValues => {
        const withoutRemoveArr = filter(patchValues, item => item?.op !== 'remove');
        const removeOnlyArr = filter(patchValues, item => item?.op === 'remove');
        removeOnlyArr.sort((itemA, itemB) => itemB?.path.split('/').pop() - itemA?.path.split('/').pop());
        const sortedPatchesArr = [];
        return sortedPatchesArr.concat(withoutRemoveArr, removeOnlyArr);
    };

    const options = {
        method: 'PATCH',
        headers,
        body: JSON.stringify(sortPatches(patches)),
    };

    const result = yield call(apiFetch, getUrl(endpoint), options);
    if (Store().getState().dialog.get(LOADING_DIALOG)) {
        yield put(hideDialog(LOADING_DIALOG));
    }
    return result;
}

const mapToPatchReducerV2 = (patches, value, key) => {
    const mappedPatches = patches;
    const path = `/${key}`;
    const values = isImmutable(value) ? value.toJS() : value;
    const operation = 'replace';
    mappedPatches.push({
        op: operation,
        path,
        value: values,
    });
    return mappedPatches;
};

export function* PATCHv2(endpoint, headers, data) {
    const patches = data.reduce(mapToPatchReducerV2, []);

    const options = {
        method: 'PATCH',
        headers,
        body: JSON.stringify(patches),
    };

    const result = yield call(apiFetch, getUrl(endpoint), options);
    if (Store().getState().dialog.get(LOADING_DIALOG)) {
        yield put(hideDialog(LOADING_DIALOG));
    }

    return result;
}

export function extractErrorMessage(result) {
    let errorResult = [];
    if (result.error != null) {
        try {
            const parsedError = JSON.parse(result.error);
            errorResult = [];
            Object.values(parsedError).forEach(value => {
                if (Array.isArray(value)) {
                    const messages = value.filter(x => typeof x === 'string' || x instanceof String);
                    errorResult.push(messages);
                }
            });
        } catch (e) {
            // ignore
        }
    }

    return errorResult;
}

export function* autoHideMessages(screenKey = ACTIVE_SCREEN_KEY) {
    yield delay(5000);
    yield put(removeAllScreenMessages(screenKey));
}

export function* autoHideMessagesAfter(milliseconds, screenKey = ACTIVE_SCREEN_KEY) {
    yield delay(milliseconds);
    yield put(removeAllScreenMessages(screenKey));
}

// Local Functions
export const convertMapToPatches = mapObj => mapObj.reduce(mapToPatchReducer, []);

export const getOriginPartnerId = () => {
    return Store().getState().oidc.getIn(['user', 'profile', 'origin_partner_id']);
};
